import type {ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';

import type {
  AvatarLabelProps,
  AvatarLayout,
} from '@/components/ui/avatar-label';
import {AvatarWithLabel} from '@/components/ui/avatar-label';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {useAuthorize} from '@/hooks/useAuth';
import type {Resident} from '@/services/graphqlHooks';
import {cn} from '@/utils/ui';

export type ResidentAvatarProps = Pick<
  AvatarLabelProps,
  'size' | 'className'
> & {
  resident: Resident;
  layout?: AvatarLayout;
  noName?: boolean;
  additionalInfo?: ReactNode;
  handleEdit?: () => void;
};

const ResidentAvatar = ({
  resident,
  size = 'default',
  layout = 'horizontal',
  noName,
  additionalInfo,
  handleEdit,
  className,
}: ResidentAvatarProps) => {
  const navigate = useNavigate();
  const canViewResident = useAuthorize('VIEW_RESIDENT');

  const firstName = resident.name?.firstName || '';
  const lastName = resident.name?.lastName || '';
  const name = `${firstName} ${lastName}`;

  const handleClick = (evt: React.MouseEvent) => {
    if (!canViewResident) return;
    evt.stopPropagation();
    navigate(`/residents/${resident.id}`);
  };

  const label = noName ? undefined : additionalInfo ? (
    <span
      className={cn(
        'flex flex-col gap-0.5',
        layout === 'vertical' ? 'items-center' : 'items-start'
      )}
    >
      <span>{name}</span>
      {additionalInfo}
    </span>
  ) : (
    name
  );

  return (
    <TooltipWrapper title={name}>
      <div
        className={cn({'cursor-pointer': canViewResident}, className)}
        onClick={handleClick}
      >
        <AvatarWithLabel
          src={resident.avatar || undefined}
          fallback={`${firstName[0]}${lastName[0]}`}
          label={label}
          color="primary"
          size={size}
          layout={layout}
          handleEdit={handleEdit}
        />
      </div>
    </TooltipWrapper>
  );
};

export default ResidentAvatar;
